<template>
  <div class="test-panel-container">
    <!-- Test buttons - Vertical layout (only 3 buttons) -->
    <div class="test-buttons-vertical">
      <button 
        @click="handleTestWarning" 
        :disabled="!isConnected || loading"
        class="test-btn-small btn-warning"
        :class="{ 'opacity-50 cursor-not-allowed': !isConnected || loading }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
        <span v-if="!loading || currentTest !== 'warning'">Test Warning</span>
        <span v-else class="animate-pulse">Testing...</span>
      </button>
      
      <button 
        @click="handleTestAlarm" 
        :disabled="!isConnected || loading"
        class="test-btn-small btn-danger"
        :class="{ 'opacity-50 cursor-not-allowed': !isConnected || loading }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
        </svg>
        <span v-if="!loading || currentTest !== 'alarm'">Test Alarm</span>
        <span v-else class="animate-pulse">Testing...</span>
      </button>
      
      <button 
        @click="handleCancelAlarm" 
        :disabled="!isConnected || loading"
        class="test-btn-small btn-secondary"
        :class="{ 'opacity-50 cursor-not-allowed': !isConnected || loading }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.143 17.082a24.248 24.248 0 0 0 3.844.148m-3.844-.148a23.856 23.856 0 0 1-5.455-1.31 8.964 8.964 0 0 0 2.3-5.542m3.155 6.852a3 3 0 0 0 5.667 1.97m1.965-2.277L21 21m-4.225-4.225a23.81 23.81 0 0 0 3.536-1.003A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6.53 6.53m10.245 10.245L6.53 6.53M3 3l3.53 3.53" />
        </svg>
        <span v-if="!loading || currentTest !== 'cancel'">Cancel Alarm</span>
        <span v-else class="animate-pulse">Cancelling...</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useDeviceStore } from '@/stores/device';

const deviceStore = useDeviceStore();

// Reactive state
const loading = ref(false);
const currentTest = ref(null);

// Computed properties
const isConnected = computed(() => deviceStore.isConnected);

/**
 * Handle Test Warning button click
 */
async function handleTestWarning() {
  if (!isConnected.value || loading.value) return;
  
  loading.value = true;
  currentTest.value = 'warning';
  
  try {
    await deviceStore.protocol.testWarning();
  } catch (err) {
    console.error('Test warning failed:', err);
  } finally {
    loading.value = false;
    currentTest.value = null;
  }
}

/**
 * Handle Test Alarm button click
 */
async function handleTestAlarm() {
  if (!isConnected.value || loading.value) return;
  
  loading.value = true;
  currentTest.value = 'alarm';
  
  try {
    await deviceStore.protocol.testAlarm();
  } catch (err) {
    console.error('Test alarm failed:', err);
  } finally {
    loading.value = false;
    currentTest.value = null;
  }
}

/**
 * Handle Cancel Alarm button click
 */
async function handleCancelAlarm() {
  if (!isConnected.value || loading.value) return;
  
  loading.value = true;
  currentTest.value = 'cancel';
  
  try {
    await deviceStore.protocol.cancelAlarm();
  } catch (err) {
    console.error('Cancel alarm failed:', err);
  } finally {
    loading.value = false;
    currentTest.value = null;
  }
}
</script>

<style scoped>
.test-panel-container {
  width: 100%;
}

.test-buttons-vertical {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.test-btn-small {
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
}

.test-btn-small:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.test-btn-small:active:not(:disabled) {
  transform: translateY(0);
}

.btn-warning {
  background-color: #fbbf24;
  color: #78350f;
  border-color: #f59e0b;
}

.btn-warning:hover:not(:disabled) {
  background-color: #f59e0b;
  border-color: #d97706;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
  border-color: #dc2626;
}

.btn-danger:hover:not(:disabled) {
  background-color: #dc2626;
  border-color: #b91c1c;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
  border-color: #4b5563;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #4b5563;
  border-color: #374151;
}
</style>
